// LIBRERIE/IMPORTS
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// COMPONENTI
import NotFoundProperty from '../Error/NotFoundProperty';

// CONFIGURAZIONI & TIPI
import { Property } from '../../types/property';
import appConfig from '../../config/appConfig';

// FOGLI DI STILE
import styles from './ImmobiliList.module.css';

interface ImmobiliListProps {
    properties: Property[];
    loading: boolean;
    error: any;
    basePath: string;
    labelSold: boolean;
}

const ImmobiliList: React.FC<ImmobiliListProps> = ({ properties, loading, error, basePath, labelSold }) => {
    const { t, i18n } = useTranslation();

    if (!loading && !error && properties.length === 0) {
        return <NotFoundProperty />;
    }

    const getPropertyName = (property: Property) => {
        return `${property.tipologyDetails.name}${t('property.inAddress', ' a ') as string}${property.addressDetails.quartiereName || property.addressDetails.frazioneName || property.addressDetails.comuneName}`;
    };

    const langPrefix = appConfig.integrations.multilang ? `/${i18n.language}` : '';

    return (
        <>
            {properties.map((property: Property) => (
                <Link className="property-list-card mb-3" to={`${langPrefix}/${basePath}/${property.id}`} key={property.id}>
                    <div className="row align-items-stretch">
                        <div className="col-12 col-lg-4 col-xl-5 ml-0 pr-lg-0">
                            <div className={styles.propertyImageContainer}>
                                <img
                                    className={styles.propertyImage}
                                    src={property.mainImage}
                                    alt={(t('property.card.imageAltBase', 'Immagine proprietà') as string) + ` ${getPropertyName(property)}`}
                                />
                                {labelSold ? (
                                    <div className={styles.propertyLabel}>
                                        <span className={`badge ${styles.badgesuccessi}`}>
                                            VENDUTO DA NOI
                                        </span>
                                    </div>
                                ) : (
                                    <div className={styles.propertyLabel}>
                                        {property.contractDetails?.name && (
                                            <span className={`badge ${styles.badge}`}>
                                                {t(`property.contractTypes.${property.contractDetails.name}`, property.contractDetails.name) as string}
                                            </span>
                                        )}
                                        {property.auctionDetails?.auction && (
                                            <span className={`badge ${styles.badge} ml-2`}>
                                                {t('property.auction')}
                                            </span>
                                        )}
                                    </div>

                                )}

                                {property.exclusive && (
                                    <div className={styles.exclusiveBox}>
                                        <span className={styles.exclusiveText}>{t('property.exclusive', 'Esclusiva') as string}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="col-12 col-lg-8 col-xl-7 pl-lg-0">
                            <div className={`${styles.propertyDetails} h-100`}>
                                <h5 className={styles.propertyTitle}>
                                    {(property.tipologyDetails.name + (t('property.inAddress', ' a ') as string) +
                                        (property.addressDetails.quartiereName || property.addressDetails.frazioneName || property.addressDetails.comuneName)) as string}
                                </h5>

                                <p className={styles.propertyAddress}>
                                    <i className="fas fa-map-marker-alt"></i>
                                    {property.addressDetails.frazioneName || property.addressDetails.quartiereName ?
                                        `${property.addressDetails.frazioneName || property.addressDetails.quartiereName}, ${property.addressDetails.comuneName} (${property.addressDetails.provinciaSigla ?? ""})`
                                        : property.addressDetails.comuneName ?
                                            `${property.addressDetails.comuneName} (${property.addressDetails.provinciaSigla ?? ""})`
                                            : property.addressDetails.provinciaName ?? ''
                                    }
                                </p>

                                <ul className={styles.propertyInfo}>
                                    {property.rooms != null && property.rooms !== '0' && (
                                        <li>
                                            <i className="fas fa-bed"></i>
                                            <span>{property.rooms} {(parseInt(String(property.rooms), 10) === 1 ? t('common.room', 'Camera') : t('common.rooms', 'Camere')) as string}</span>
                                        </li>
                                    )}
                                    {property.bathrooms != null && property.bathrooms !== 0 && (
                                        <li>
                                            <i className="fas fa-bath"></i>
                                            <span>{property.bathrooms} {(property.bathrooms === 1 ? t('common.bathroom', 'Bagno') : t('common.bathrooms', 'Bagni')) as string}</span>
                                        </li>
                                    )}
                                    {property.sqmt && property.sqmt !== 0 && (
                                        <li>
                                            <i className="fas fa-vector-square"></i>
                                            <span>{property.sqmt} m²</span>
                                        </li>
                                    )}
                                </ul>

                                <div className={styles.propertyFooter}>

                                    <div className={styles.propertyPrice}>
                                        {property.priceDetails.reservedNegotiation ? (
                                            // CASO 1: Trattativa riservata
                                            <span className={styles.reserved}>{t('property.priceReserved', 'Tr. Riservata') as string}</span>
                                        ) : property.auctionDetails.auction ? (
                                            // CASO 2: Immobile in asta
                                            <>
                                                da € {new Intl.NumberFormat('it-IT').format(property.priceDetails.price)}
                                            </>
                                        ) : (
                                            // CASO 3: Prezzo standard (con o senza negoziabilità)
                                            <>
                                                € {new Intl.NumberFormat('it-IT').format(property.priceDetails.price)}
                                                {property.priceDetails.negotiable && (
                                                    <span className={styles.negotiable}>{`(${(t('common.negotiable', 'Tr.') as string)})`}</span>
                                                )}
                                            </>
                                        )}
                                    </div>

                                    <div className={styles.propertyActions}>
                                        <a
                                            className="btn btn-primary"
                                            href={`${langPrefix}/${basePath}/${property.id}`}
                                            aria-label={t('property.card.ariaViewDetails', `Visualizza dettagli per ${getPropertyName(property)}`, { propertyName: getPropertyName(property) }) as string}
                                        >
                                            {t('common.details', 'Dettagli') as string}
                                        </a>
                                        <div className={styles.propertyReference}>
                                            <span className={styles.refLabel}>{(t('property.reference', 'Rif.') as string)} </span> {property.ref}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Link>
            ))}
        </>
    );
};

export default ImmobiliList;